<script lang="ts">
  import Button from "$components/+design-system/Button/Button.svelte";
  import InviteLink from "./InviteLink.svelte";
  import api from "api";
  import cx from "classnames";
  import { alerts } from "app/stores/alert";
  import { inviteCode, community } from "app/stores/community";
  import { canEditCommunitySettings } from "app/stores/permissions";
  import { onMount } from "svelte";
  import type { AxiosError } from "axios";
  import CreationTimeInterval from "$components/+shared/CreationTimeInterval.svelte";
  import { InviteMemberErrorEnum } from "@outverse-video/outverse-api-client-lib";
  import { communityJoinUrl } from "app/utils/urls";
  import { PaperPlane } from "$components/+shared/OVIcon";

  onMount(() => {
    community.getInviteCode();
  });

  // INVITE BY EMAIL
  let email: string = "";
  let errorMessage: string = "";

  // INVITE LINK
  $: showGenerate = $canEditCommunitySettings && !!$community.isPrivate;
  const defaultInvite = communityJoinUrl({ communitySlug: $community.slug }, true);
  let inviteLink = defaultInvite;
  $: if ($inviteCode.code) {
    inviteLink = `${defaultInvite}/${$inviteCode.code}`;
  }

  const handleSubmit = () => {
    //   email validation
    let isEmailEmpty = email === "";
    const validEmailPattern: RegExp = /^\S+@\S+$/i;
    let isEmailValid = validEmailPattern.test(email);

    errorMessage = isEmailEmpty
      ? "Please enter an email."
      : !isEmailValid
        ? "Please enter a valid email."
        : "";

    if (isEmailEmpty || !isEmailValid) {
      return;
    } else {
      inviteUser({ email }).then((success) => {
        if (success) email = "";
      });
    }
  };

  const inviteUser = async ({ email }: { email: string }) => {
    try {
      await api.communitiesInviteCodeInviteMember({
        communitySlug: $community.slug,
        inviteMember: {
          email,
        },
      });

      alerts.success(`An invite has been sent to ${email}`);
      return true;
    } catch (err: unknown) {
      // @ts-ignore
      const errorType = (err as AxiosError)?.response?.data.error;
      let message = "A problem occurred";

      if (errorType === InviteMemberErrorEnum.MemberAlreadyExists) {
        message = "A member with that email already exists in your community";
      } else if (errorType === InviteMemberErrorEnum.TooSoon) {
        message = "Please wait 30 seconds before attempting to resend invite";
      }

      alerts.error(message);
      return false;
    }
  };
</script>

<div
  class="flex max-h-[820px] w-full animate-fade-in flex-col items-center
  gap-6 overflow-hidden rounded-[15px] bg-white p-5 text-gray-500 dark:bg-gray-700/80 dark:text-gray-100"
>
  <section class="w-full space-y-2">
    <h2 class="text-lg font-medium">Invite new members</h2>
    <p class="text-sm dark:text-gray-400">
      Invite members to your community, type their email below to send an email invite.
    </p>
    <div class="flex flex-col pt-2">
      <form on:submit={() => handleSubmit()}>
        <div class="flex justify-between gap-2 whitespace-nowrap">
          <input
            bind:value={email}
            name="email"
            placeholder="Type email"
            aria-label="Type email of new member"
            class={cx(
              "w-full rounded-md px-3 py-2 text-sm transition-colors",
              "font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-100",
              "placeholder:text-gray-400/80 dark:placeholder:dark:text-neutral-500",
              "border border-gray-300 dark:border-gray-500",
              "focus:border-gray-900/70 focus:outline-none focus:dark:border-gray-400"
            )}
          />
          <Button
            label="Send invite"
            htmlButtonType="submit"
            size="large"
            variant="system"
            iconRight={PaperPlane}
          />
        </div>
        {#if errorMessage}
          <p class="pt-2 text-xs font-semibold text-red-500 dark:text-red-400">
            {errorMessage}
          </p>
        {/if}
      </form>
    </div>
  </section>
  <section class="w-full space-y-2">
    <h2 class="text-lg font-medium">Invite link</h2>
    <p class="text-sm dark:text-gray-400">
      Use this link to invite people to join your community. Click the link to copy it to your
      clipboard.
    </p>
    <InviteLink {showGenerate} {inviteLink} />
    {#if $community.isPrivate}
      <p class="inline-flex gap-0.5 pt-1 text-xs text-gray-400">
        {#if $inviteCode.codeRefreshedAt}
          Last generated
          <CreationTimeInterval
            fontClasses=""
            time={$inviteCode.codeRefreshedAt}
            modified={new Date().toISOString()}
          />
        {/if}
      </p>
    {/if}
  </section>
</div>
